import {
  CsvFileUploadImportConfig,
  FileUploadImportConfig,
  LocalImportConfig,
  WebhookImportConfig,
} from '@oneschema/react';
import { Company } from '~/types/company';
import { Money } from '~/types/money';
import { Account, AccountWithConsent } from './account';
import { UserDataItem, UserInfo } from './consent';
import { Counterpart } from './counterparts';
import { Flow } from './flow';
import { PaymentAttachment } from './payment-attachment';
import { Transaction } from './transaction';

export interface Payment {
  id: string;
  status: PaymentStatus;
  amount: Money;
  payeeName: string;
  payeeIdentifier: string;
  reason: string;
  instant?: boolean;
  requestedExecutionAt: string;
  company: {
    id: Company['id'];
  };
  account?: AccountWithConsent;
  debug: boolean;
  flows?: Flow[];
  redirectUrl?: string;
  userData: UserDataItem[];
  userInfo?: UserInfo | null;
  createdAt: string;
  attachments?: PaymentAttachment[];
  metadata?: {
    summaryEmailAddresses?: string[] | null;
    reached_timeout?: boolean | null;
    changed_status_from_pending?: boolean | null;
    pdf_generated_for_status?: PaymentStatus | null;
  };
  pdfStatementUrl?: string;
  transactionReferences?: string[];
  transactions?: Transaction[];
  parent?: Payment;
  retryAttempts: Payment[];
  counterpart?: Counterpart;
}

export interface PaymentBeneficiaryCheckPayload {
  payeeName: string;
  payeeIdentifier: string;
  accountId: string;
}

export interface PaymentBeneficiaryCheckResponse {
  matchStatus: BeneficiaryMatchStatus;
  status: BeneficiaryCheckStatus;
  suggestedName: string | null;
  sourceBeneficiaryId: string;
}

export interface PaymentBeneficiaryApprovalPayload {
  accountId: string;
  sourceBeneficiaryId: string;
}

export enum PaymentStatus {
  Canceled = 'CANCELED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Succeeded = 'SUCCEEDED',
  Accepted = 'ACCEPTED',
  Timeout = 'TIMEOUT',
  Draft = 'DRAFT',
  Scheduled = 'SCHEDULED',
}

export enum BeneficiaryCheckStatus {
  Pending = 'PENDING',
  Verified = 'VERIFIED',
  Rejected = 'REJECTED',
}

export enum BeneficiaryMatchStatus {
  FullMatch = 'FULL_MATCH',
  CloseMatch = 'CLOSE_MATCH',
  NoMatch = 'NO_MATCH',
  Exempted = 'EXEMPTED',
  VerificationCheckNotPossible = 'VERIFICATION_CHECK_NOT_POSSIBLE',
}

export const PaymentResourceType = 'payment';

export const PaymentQueryKeys = {
  all: ['Payments'],
  single: (id?: Payment['id'] | null) => ['Payments', id],
  list: (companyId: string, filters: PaymentFetchParameter) => ['Payments', { companyId }, filters],
  companyInfinityList: (companyId: string, filters: PaymentFetchParameter) => [
    'Payments',
    { companyId },
    'Infinite',
    filters,
  ],
  fullDetails: (id?: Payment['id'] | null) => ['Payments', id, 'FullDetails'],
  latestByPayeeIdentifier: (companyId: string, bankIdentifier: string) => [
    'Payments',
    'LatestByPayeeIdentifier',
    companyId,
    bankIdentifier,
  ],
};

export interface CreatePayment extends Omit<
  Payment,
  | 'id'
  | 'account'
  | 'flows'
  | 'status'
  | 'source'
  | 'userData'
  | 'userInfo'
  | 'createdAt'
  | 'debug'
  | 'counterpart'
  | 'parent'
  | 'retryAttempts'
  | 'requestedExecutionAt'
> {
  debug?: boolean;
  requestedExecutionAt?: string;
  flows: {
    id: Flow['id'];
  }[];
  account: {
    id: Account['id'];
  };
  counterpart?: {
    id: Counterpart['id'];
  } | null;
  parent?: {
    id: Payment['id'];
  } | null;
  metadata: {
    summaryEmailAddresses: string[] | null;
  };
}

export interface UpdatePaymentData {
  paymentId: string;
  userData: {
    key: string;
    value: string;
  }[];
  debug: boolean;
}

export interface PaymentFetchParameter {
  companyId: string | number | null;
  createdAt?: string | null;
  dateRange?: [string | null, string | null];
  payeeIdentifier?: string | null;
  status?: PaymentStatus[] | null;
  search?: string;
  pageParam?: string;
  pageSize?: number;
}

export const PaymentsKeys = {
  all: ['Payments'],
  metadata: (filters: PaymentFetchParameter) => ['Payments', filters],
};

export interface UpdatePaymentStatus {
  paymentId: string;
  status: PaymentStatus;
}

export interface PaymentFullDetails extends Payment {
  company: Company;
  account: Account;
  counterpart: Counterpart;
}

export type PaymentsSortFields = 'requestedExecutionAt';

export interface PaymentsFiltersFields {
  search: string;
  dateRange: [string | null, string | null];
  status: PaymentStatus[];
}

export interface BulkPaymentImportConfig {
  baseUrl: string;
  clientId: string;
  customizationKey: string;
  templateKey: string;
  importConfig: LocalImportConfig | WebhookImportConfig | CsvFileUploadImportConfig | FileUploadImportConfig;
  userJwt: string;
  devMode: boolean;
}
